import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlightIcon from '@mui/icons-material/Flight';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ActivityTrackerCard from '../../components/Cards/ActivityTrackerCard';
import AlertCard from '../../components/Cards/AlertCard';
import DailyParametersCard from '../../components/Cards/DailyParametersCard';
import MultiDatePicker from '../../components/documented/MultiDatePicker/MultiDatePicker';
import NoData from '../../components/NoData';
import ControlVisibility from '../../components/Settings/ControlVisibility';
import { useClientTheme } from '../../helpers/hooks/useClientTheme';
import {
  compareTimezoneToHCP,
  getDateRanges,
  isDatesEqual,
  isDevelopment,
  resetTimeforDate,
} from '../../helpers/utilityFunctions/utilityFunctions';
import { DateRange } from '../../model/dateRange';
import { HeatmapData } from '../../model/passive/heatmapData';
import { PassiveParameterType } from '../../model/passive/passiveParameterType';
import { Temperature } from '../../model/temperature';
import {
  clearSelectedDailyMetrics,
  getDailyMetricDatesByRange,
  getDailyMetrics,
  setDailyMetricDate,
} from '../../redux/actions/dailyMetricActions';
import { useAppThunkDispatch } from '../../redux/configureStore';
import {
  dailyMetricDates,
  selectedDailyMetrics,
  selectedMetricDate,
} from '../../redux/selectors/dailyMetricsSelectors';

interface Props {
  selectedPatientId?: string;
}

export const DailyMetricsPage: React.FC<Props> = ({ selectedPatientId }) => {
  const dispatch = useAppThunkDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['dailyMetrics', 'common']);
  const { theme } = useClientTheme();

  //Dates
  const dates = useSelector(dailyMetricDates);
  const [selectedDate, setSelectedDate] = React.useState<Date>();
  const selectedDailyMetricDate = useSelector(selectedMetricDate);
  const [selectedMonth, setSelectedMonth] = React.useState<Date>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const selectedData = useSelector(selectedDailyMetrics);

  //this triggers when date in selector changes
  useEffect(() => {
    if (selectedDate === undefined) return;

    const dateToFind = resetTimeforDate(selectedDate);
    const index = dates.findIndex((date: Date) => {
      return date.toString() === dateToFind;
    });
    changeDailyMetric(index.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  //this fetches the data for each month when the month changes
  useEffect(() => {
    if (selectedMonth !== undefined) {
      setIsLoading(true);
      const dateRange: DateRange = getDateRanges(selectedMonth);
      dispatch(
        getDailyMetricDatesByRange(
          selectedPatientId || '',
          dateRange.startDate,
          dateRange.endDate
        )
      ).then(() => {
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => {
    //check if a selected date exists in store, if so, try and set that as date here in the first instance. If not, try and populate the page with todays date
    const date = selectedDailyMetricDate || new Date();
    const dateRange: DateRange = getDateRanges(date, true);
    dispatch(
      getDailyMetricDatesByRange(
        selectedPatientId || '',
        dateRange.startDate,
        dateRange.endDate
      )
    ).then((response: any) => {
      //this is a bit of a hack - async sometimes isnt updating on time when fetching dates
      if (!response || !response.payload) return;
      if (selectedDailyMetricDate) {
        // Honour the previously selected date — changeDailyMetric fires via
        // the selectedDate useEffect once selectedDate is set
        setSelectedDate(new Date(date));
      } else if (Array.isArray(response.payload) && response.payload.length) {
        const latestDate = response.payload.reduce(function (a: any, b: any) {
          return a > b ? a : b;
        });
        setSelectedDate(new Date(latestDate));
      } else {
        setSelectedDate(new Date());
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeDailyMetric = (index: string) => {
    dispatch(clearSelectedDailyMetrics());
    if (index === '-1') return;
    const selectedDate = dates[Number(index)];
    dispatch(setDailyMetricDate(selectedDate));
    dispatch(
      getDailyMetrics(
        selectedPatientId!,
        selectedDate,
        theme.metrics.temperature.unit
      )
    );
  };

  const handleClick = (arg: string) => {
    navigate(
      `/dashboard/patients/${selectedPatientId}/passive/${arg.toLowerCase()}`
    );
  };

  const handleDailyParamClick = () => {
    navigate(`/dashboard/patients/${selectedPatientId}/dailyparameters`);
  };

  const handleActivityTrackerClick = () => {
    const route = isDevelopment() ? 'activitytracker-dev' : 'activitytracker';
    navigate(`/dashboard/patients/${selectedPatientId}/passive/${route}`);
  };

  const handleTemperatureClick = (_arg: string) => {
    const route = isDevelopment() ? 'temperature-dev' : 'temperature';
    navigate(`/dashboard/patients/${selectedPatientId}/passive/${route}`);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography variant="h5" sx={{ mt: 1, pr: 2 }}>
          <strong>{t('title')}</strong>
        </Typography>

        <MultiDatePicker
          dates={dates}
          selectedDate={selectedDate}
          isLoading={isLoading}
          setSelectedDate={setSelectedDate}
          setSelectedMonth={setSelectedMonth}
        />

        {/* Temporarily hidden - timezone icons causing issues */}
        {/* <Box
          sx={{
            ml: '20px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {selectedData &&
          selectedData?.dailyMetricsStatistics.hasBeenTraveling ? (
            <Tooltip title="This patient has been traveling across time zones during the selected day. All the times are reported in the patient's time zone.">
              <FlightIcon
                sx={{
                  transform: 'scale(1.3)',
                }}
                color="error"
              />
            </Tooltip>
          ) : null}
          {selectedData &&
          compareTimezoneToHCP(
            selectedData?.dailyMetricsStatistics.endOfDayTimezone,
            selectedData?.dailyMetricsStatistics.hasBeenTraveling,
            selectedDate
          ) ? (
            <Tooltip title="This patient is in a different time zone than you on that day. All the times are reported in the patient’s time zone.">
              <AccessTimeIcon
                sx={{
                  transform: 'scale(1.3)',
                }}
                color="error"
              />
            </Tooltip>
          ) : null}
        </Box> */}
      </Box>

      {selectedData ? (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <ControlVisibility value="pressureDistribution">
            <Grid>
              <AlertCard
                isAlert={isDatesEqual(
                  new Date(selectedData?.latestAlertsPressure?.dateTime!),
                  new Date(selectedDate!)
                )}
                parameterType={PassiveParameterType.AlarmPressure}
                onClick={handleClick}
                popoverContent={t('cards.pressureAlert.popover')}
                heatmapData={
                  new HeatmapData(
                    selectedData?.latestAlertsPressure
                      ? selectedData?.latestAlertsPressure.sensorDataLeft
                      : undefined,
                    selectedData?.latestAlertsPressure
                      ? selectedData?.latestAlertsPressure.sensorDataRight
                      : undefined
                  )
                }
              />
            </Grid>
          </ControlVisibility>
          <ControlVisibility value="temperatureDistribution">
            <Grid>
              <AlertCard
                isAlert={isDatesEqual(
                  new Date(selectedData?.latestAlertsTemperature?.dateTime!),
                  new Date(selectedDate!)
                )}
                parameterType={PassiveParameterType.AlarmTemperature}
                onClick={handleTemperatureClick}
                popoverContent={t('cards.temperatureAlert.popover', {
                  threshold:
                    theme.metrics.temperature.unit === Temperature.Celsius
                      ? t('cards.temperatureAlert.thresholdCelsius')
                      : t('cards.temperatureAlert.thresholdFahrenheit'),
                })}
                heatmapData={
                  new HeatmapData(
                    selectedData?.latestAlertsTemperature
                      ? selectedData?.latestAlertsTemperature.sensorDataLeft
                      : undefined,
                    selectedData?.latestAlertsTemperature
                      ? selectedData?.latestAlertsTemperature.sensorDataRight
                      : undefined
                  )
                }
                unit={theme.metrics.temperature.unit}
              />
            </Grid>
          </ControlVisibility>
          <ControlVisibility value="dailyParameters">
            <Grid>
              <DailyParametersCard
                large
                title={t('cards.dailyParameters.title')}
                onClick={() => handleDailyParamClick()}
                popoverContent={t('cards.dailyParameters.popover')}
                data={selectedData?.dailyMetrics}
              />
            </Grid>
          </ControlVisibility>
          <ControlVisibility value="activityTracker">
            {selectedData && selectedData.dailyMetrics.length > 0 ? (
              <Grid>
                <ActivityTrackerCard
                  large
                  title={t('cards.activityTracker.title')}
                  onClick={() => handleActivityTrackerClick()}
                  popoverContent={t('cards.activityTracker.popover')}
                  data={selectedData.dailyMetrics}
                />
              </Grid>
            ) : null}
          </ControlVisibility>
        </Grid>
      ) : (
        <NoData message={t('common:noData')} />
      )}
    </Box>
  );
};

export default DailyMetricsPage;
